import Home from "../components/main-pages/Home";
import Head from "next/head";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";
import HreflangLinks from "../components/common/HrefLinkTags";
// API
import {
  fatchHomeCaseStudies,
  fatchHomePortfolio,
  fatchHomeIndustries,
  fetchBlogData,
} from "../components/http/commonApi.js";
import { getGoogleReviews } from "../lib/googleReviews";
import { getHomeSchema } from "../lib/homeSchema";

const SITE = "https://www.prism-me.com";

export default function Homepage({
  initialPortfolio = [],
  initialIndustries = [],
  initialCaseStudies = [],
  initialBlogs = [],
  googleReviews = null,
}) {
  const { t } = useTranslation("home");
  const router = useRouter();
  const lang = router.locale || "en";

  const isArabic = lang === "ar";

  const schema = getHomeSchema(isArabic);

  return (
    <>
      <HreflangLinks enPath="/" arPath="/ar" />
      <Head>
        <title>{t("home.seo.metaTitle")}</title>
        <meta name="description" content={t("home.seo.metaDescription")} />
        <link rel="canonical" href={isArabic ? `${SITE}/ar` : SITE} />

        {/* OG Tags */}
        <meta
          property="og:title"
          content={
            isArabic
              ? "بريزم ديجيتال | أفضل شركة للتسويق الإلكتروني في الإمارات"
              : "Digital Marketing Agency | Prism Digital"
          }
        />
        <meta
          property="og:description"
          content={
            isArabic
              ? "بريزم ديجيتال هي شركة رائدة في مجال التسويق الإلكتروني..."
              : "Leading digital marketing agency in Dubai offering SEO, PPC, social media..."
          }
        />
        <meta
          property="og:url"
          content={isArabic ? `${SITE}/ar/` : `${SITE}/`}
        />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content={isArabic ? "ar_AE" : "en_US"} />
        <meta property="og:site_name" content="Prism Digital" />

        {/* Structured Data JSON-LD — single @graph block */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
        />
      </Head>

      <Home
        initialPortfolio={initialPortfolio}
        initialIndustries={initialIndustries}
        initialCaseStudies={initialCaseStudies}
        initialBlogs={initialBlogs}
        googleReviews={googleReviews}
      />
    </>
  );
}

export async function getStaticProps({ locale }) {
  const lang = locale || "en";

  // The helper never throws (returns null on failure) and is cached for 2 days
  const googleReviews = await getGoogleReviews();

  let initialPortfolio = [];
  let initialIndustries = [];
  let initialCaseStudies = [];
  let initialBlogs = [];

  try {
    const [csRes, pfRes, indRes, blogRes] = await Promise.all([
      fatchHomeCaseStudies(lang),
      fatchHomePortfolio(lang),
      fatchHomeIndustries(lang),
      fetchBlogData({ page: 1, limit: 6, lang }),
    ]);

    initialCaseStudies = (csRes?.data ?? []).sort(
      (a, b) => new Date(b.created_at) - new Date(a.created_at),
    );
    initialPortfolio = pfRes?.data?.items ?? [];
    initialIndustries = indRes?.data ?? [];
    initialBlogs = blogRes?.data?.blogs ?? [];
  } catch (e) {
    // fail silently, keep empty arrays
  }

  return {
    props: {
      ...(await serverSideTranslations(lang, [
        "header",
        "footer",
        "home",
        "common",
      ])),
      initialPortfolio,
      initialIndustries,
      initialCaseStudies,
      initialBlogs,
      googleReviews,
    },
    revalidate: 3600,
  };
}
